/**
 * The latest live shift messages of the machines of a line
 * (`edge-data.<serial>.shift.current.*` in the organization's <ORG>_SHIFT
 * stream), read with JetStream Direct Get on the app's NATS connection every
 * `intervalMs`.
 *
 * No consumer is created per machine: consumer churn on that stream once
 * amplified an outage (see liveShift.worker.ts), and a few cheap
 * request/replies per round are enough for a line overview. A stream without
 * Direct Get (allow_direct off) gives no data here; the machine Live Shift tab
 * still works through its worker.
 */

import { useEffect, useMemo, useState } from "react";
import { getValidToken } from "../../auth/tokenManager";
import type { LiveShiftData, LiveStatusTransition, TopStatusData } from "../../hooks/useLiveShiftData";
import { getActiveOrgAlias } from "../../services/demoMode";
import { natsClient } from "../../services/NATSClient";
import { getShiftStreamName } from "../../utils/tokenUtils";

export interface MachineLive {
  shift?: LiveShiftData;
  transition?: LiveStatusTransition;
  topStops?: TopStatusData;
}

/** Requests in flight at once. */
const CONCURRENCY = 8;
/** Retry while the app's NATS connection is not up yet. */
const NOT_CONNECTED_RETRY_MS = 2000;

interface DirectGet {
  getMessage(stream: string, query: { last_by_subj: string }): Promise<{ data: Uint8Array }>;
}

/**
 * `full`: also each machine's last status transition (the open tab);
 * otherwise only the shift message, enough to know whether the line has live
 * data.
 */
export function useLineLiveShift(sns: string[], options: { full: boolean; intervalMs: number }) {
  const { full, intervalMs } = options;
  const [state, setState] = useState<{ data: Map<string, MachineLive>; loading: boolean }>({
    data: new Map(),
    loading: true,
  });
  const snsKey = sns.join(",");

  useEffect(() => {
    const list = snsKey ? snsKey.split(",") : [];
    if (list.length === 0) {
      setState({ data: new Map(), loading: false });
      return;
    }
    let cancelled = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = (ms: number) => {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void round(), ms);
    };

    const round = async () => {
      if (cancelled || inFlight) return;
      if (!natsClient.isConnected()) {
        schedule(NOT_CONNECTED_RETRY_MS);
        return;
      }
      inFlight = true;
      try {
        const alias = getActiveOrgAlias(await getValidToken());
        if (!alias || cancelled) return;
        const stream = getShiftStreamName(alias);
        const direct = (natsClient.jetstreamManager() as unknown as { direct: DirectGet }).direct;

        const jobs = list.flatMap((sn) =>
          (full ? (["shift", "transition"] as const) : (["shift"] as const)).map((part) => ({ sn, part })),
        );
        const next = new Map<string, MachineLive>();
        let i = 0;
        const worker = async () => {
          while (!cancelled && i < jobs.length) {
            const { sn, part } = jobs[i++];
            const leaf = part === "shift" ? "shift" : part === "transition" ? "status.transition" : "top.status";
            try {
              const m = await direct.getMessage(stream, {
                last_by_subj: `edge-data.${sn.padStart(5, "0")}.shift.current.${leaf}`,
              });
              const parsed: unknown = JSON.parse(new TextDecoder().decode(m.data));
              const entry = next.get(sn) ?? {};
              if (part === "shift") entry.shift = parsed as LiveShiftData;
              else if (part === "transition") entry.transition = parsed as LiveStatusTransition;
              else entry.topStops = parsed as TopStatusData;
              next.set(sn, entry);
            } catch {
              // no message for this subject (the machine sends no live data)
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker));
        if (!cancelled) setState({ data: next, loading: false });
      } catch (err) {
        console.warn("[useLineLiveShift] round failed:", err);
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      } finally {
        inFlight = false;
        schedule(intervalMs);
      }
    };

    void round();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [snsKey, full, intervalMs]);

  const hasAny = useMemo(() => [...state.data.values()].some((m) => !!m.shift), [state.data]);
  return { data: state.data, loading: state.loading, hasAny };
}
